export const LICENSE_ASSESSMENT_ORIGINAL_CODE = `import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Users, TrendingDown, AlertTriangle, CheckCircle, Settings } from 'lucide-react';

const LicenseOptimizationDashboard = () => {
  const [bufferPercentage, setBufferPercentage] = useState(15);
  const [selectedLicense, setSelectedLicense] = useState(null);

  const licenseData = [
    {
      type: 'Salesforce CPQ',
      purchased: 150,
      assigned: 119,
      utilized180: 67,
      category: 'Feature License'
    },
    {
      type: 'Sales Console',
      purchased: 85,
      assigned: 72,
      utilized180: 58,
      category: 'Feature License'
    },
    {
      type: 'Experience Cloud',
      purchased: 100,
      assigned: 85,
      utilized180: 52,
      category: 'Feature License'
    },
    {
      type: 'CRM Analytics User',
      purchased: 120,
      assigned: 89,
      utilized180: 45,
      category: 'Analytics License'
    },
    {
      type: 'Marketing Cloud Account Engagement',
      purchased: 50,
      assigned: 38,
      utilized180: 28,
      category: 'Marketing License'
    }
  ];

  // Platform license data for summary cards only
  const platformLicense = {
    purchased: 300,
    assigned: 265,
    utilized180: 172
  };

  const calculateRecommendations = (license) => {
    const buffer = bufferPercentage / 100;
    const recommended = Math.ceil(license.utilized180 * (1 + buffer));
    const potentialSavings = license.purchased - recommended;
    const utilizationRate = ((license.utilized180 / license.assigned) * 100).toFixed(1);
    const assignedUtilizationRate = ((license.utilized180 / license.purchased) * 100).toFixed(1);
    
    return {
      ...license,
      recommended,
      potentialSavings: potentialSavings > 0 ? potentialSavings : 0,
      utilizationRate,
      assignedUtilizationRate,
      waste: license.assigned - license.utilized180,
      unused: license.purchased - license.assigned
    };
  };

  const enhancedLicenseData = licenseData.map(calculateRecommendations);
  const totalPurchased = platformLicense.purchased;
  const totalAssigned = platformLicense.assigned;
  const totalUtilized = platformLicense.utilized180;
  const totalRecommended = Math.ceil(platformLicense.utilized180 * (1 + bufferPercentage / 100));
  const totalSavings = platformLicense.purchased - totalRecommended;

  const getUtilizationColor = (rate) => {
    if (rate >= 80) return '#10b981';
    if (rate >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const chartData = enhancedLicenseData.map(license => ({
    name: license.type,
    Purchased: license.purchased,
    Assigned: license.assigned,
    'Utilized (180d)': license.utilized180,
    Recommended: license.recommended
  }));

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-800 mb-1">License Utilization Analysis</h1>
          <p className="text-slate-600 text-sm">Organization: TechVentures Global • Last Updated: October 2025</p>
          <p className="text-xs text-slate-500 mt-1">Summary cards show Salesforce Platform licenses (base user count)</p>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-600 mb-1">Platform Licenses Purchased</p>
                <p className="text-2xl font-bold text-slate-800">{totalPurchased}</p>
              </div>
              <Users className="w-10 h-10 text-blue-500 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-600 mb-1">Platform Licenses Assigned</p>
                <p className="text-2xl font-bold text-slate-800">{totalAssigned}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-600 mb-1">Active Users (180 days)</p>
                <p className="text-2xl font-bold text-slate-800">{totalUtilized}</p>
              </div>
              <TrendingDown className="w-10 h-10 text-yellow-500 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-600 mb-1">Platform Utilization Rate</p>
                <p className="text-2xl font-bold text-slate-800">
                  {((totalUtilized / totalAssigned) * 100).toFixed(0)}%
                </p>
              </div>
              <Settings className="w-10 h-10 text-purple-500 opacity-20" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-800">License Breakdown by Type</h3>
              <p className="text-xs text-slate-600 mt-1">Click any row for detailed analysis</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-xs text-slate-600">Buffer</p>
                <select
                  value={bufferPercentage}
                  onChange={(e) => setBufferPercentage(Number(e.target.value))}
                  className="text-sm px-3 py-1 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={5}>5%</option>
                  <option value={10}>10%</option>
                  <option value={15}>15%</option>
                  <option value={20}>20%</option>
                  <option value={25}>25%</option>
                </select>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-600">Recommended Platform</p>
                <p className="text-xl font-bold text-green-600">{totalRecommended}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-600">Potential Platform Savings</p>
                <p className="text-xl font-bold text-red-600">{totalSavings}</p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase">
                    License Type
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-slate-600 uppercase">
                    Purchased
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-slate-600 uppercase">
                    Assigned
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-slate-600 uppercase">
                    Utilized (180d)
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-slate-600 uppercase">
                    Utilization
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-slate-600 uppercase">
                    Recommended
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-slate-600 uppercase">
                    Savings
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {enhancedLicenseData.map((license, index) => (
                  <React.Fragment key={index}>
                    <tr 
                      onClick={() => setSelectedLicense(selectedLicense === license.type ? null : license.type)}
                      className={'cursor-pointer hover:bg-slate-50 transition-colors ' + (selectedLicense === license.type ? 'bg-blue-50' : '')}
                    >
                      <td className="px-4 py-3">
                        <div>
                          <div className="text-sm font-medium text-slate-900">{license.type}</div>
                          <div className="text-xs text-slate-500">{license.category}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-slate-900 font-semibold">
                        {license.purchased}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="text-sm text-slate-900">{license.assigned}</div>
                        <div className="text-xs text-slate-500">{license.unused} unused</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="text-sm font-semibold text-green-600">{license.utilized180}</div>
                        <div className="text-xs text-slate-500">{license.waste} inactive</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center">
                          <div 
                            className="text-sm font-semibold mb-1"
                            style={{ color: getUtilizationColor(parseFloat(license.utilizationRate)) }}
                          >
                            {license.utilizationRate}%
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-1.5">
                            <div
                              className="h-1.5 rounded-full transition-all"
                              style={{
                                width: license.utilizationRate + '%',
                                backgroundColor: getUtilizationColor(parseFloat(license.utilizationRate))
                              }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-sm font-semibold text-purple-600">
                        {license.recommended}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {license.potentialSavings > 0 ? (
                          <div className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                            -{license.potentialSavings}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs">None</span>
                        )}
                      </td>
                    </tr>
                    {selectedLicense === license.type && (
                      <tr className="bg-blue-50">
                        <td colSpan={7} className="px-4 py-4">
                          <div className="grid grid-cols-2 gap-6">
                            <div>
                              <h4 className="font-semibold text-slate-800 mb-3 text-sm">Current State Analysis</h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Purchased licenses not assigned:</span>
                                  <span className="font-semibold text-slate-800">{license.unused}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Assigned but not used (180d):</span>
                                  <span className="font-semibold text-orange-600">{license.waste}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Overall utilization (vs purchased):</span>
                                  <span className="font-semibold" style={{ color: getUtilizationColor(parseFloat(license.assignedUtilizationRate)) }}>
                                    {license.assignedUtilizationRate}%
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div>
                              <h4 className="font-semibold text-slate-800 mb-3 text-sm">Optimization Recommendation</h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Active users (180d):</span>
                                  <span className="font-semibold text-green-600">{license.utilized180}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Recommended with {bufferPercentage}% buffer:</span>
                                  <span className="font-semibold text-purple-600">{license.recommended}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Potential license reduction:</span>
                                  <span className="font-semibold text-red-600">{license.potentialSavings}</span>
                                </div>
                              </div>
                              {license.potentialSavings > 0 && (
                                <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                                  <p className="text-xs text-yellow-800">
                                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                                    Consider reducing license count at next renewal
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">License Overview by Type</h3>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={130} fontSize={11} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="Purchased" fill="#94a3b8" />
              <Bar dataKey="Assigned" fill="#3b82f6" />
              <Bar dataKey="Utilized (180d)" fill="#10b981" />
              <Bar dataKey="Recommended" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800">Summary by License Category</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    License Category
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Total Purchased
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Total Assigned
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Total Utilized (180d)
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Over-Purchased
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Assigned But Unused
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {['Feature License', 'Analytics License', 'Marketing License'].map((category) => {
                  const categoryLicenses = enhancedLicenseData.filter(l => l.category === category);
                  if (categoryLicenses.length === 0) return null;
                  
                  const catPurchased = categoryLicenses.reduce((sum, l) => sum + l.purchased, 0);
                  const catAssigned = categoryLicenses.reduce((sum, l) => sum + l.assigned, 0);
                  const catUtilized = categoryLicenses.reduce((sum, l) => sum + l.utilized180, 0);
                  const catOverPurchased = catPurchased - catAssigned;
                  const catUnused = catAssigned - catUtilized;
                  
                  return (
                    <tr key={category} className="hover:bg-slate-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-slate-900">{category}</div>
                        <div className="text-xs text-slate-500">{categoryLicenses.length} license type(s)</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-semibold text-slate-900">
                        {catPurchased}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-semibold text-blue-600">
                        {catAssigned}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-semibold text-green-600">
                        {catUtilized}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                          {catOverPurchased}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
                          {catUnused}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseOptimizationDashboard;`;
